import type { ResolvedPageContextRoute } from './routeMetadata';
import type {
  PageContext,
  PageContextContribution,
  PageContextNavigationItem,
  PageContextPresentation,
  PageContextResource,
  PageContextResourceRole,
  PageContextSnapshot,
  PageContextState,
} from './types';

export type PageContextContributorId = symbol;

export interface RegisteredPageContextContribution {
  owner: string;
  pageScope: string;
  contribution: PageContextContribution;
  signature: string;
}

const RESOURCE_ROLE_PRIORITY: Record<PageContextResourceRole, number> = {
  related: 0,
  subject: 1,
  selected: 2,
};

interface CompositionParts {
  navigationOwners: Map<number, string>;
  navigationByLevel: Map<number, PageContextNavigationItem>;
  stateOwners: Map<keyof PageContextState, string>;
  state: PageContextState;
  resources: PageContextResource[];
  integration?: PageContextPresentation['integration'];
  integrationOwner?: string;
}

const warnDuplicateOwner = (field: string, firstOwner: string, secondOwner: string): void => {
  if (process.env.NODE_ENV !== 'production') {
    // eslint-disable-next-line no-console
    console.warn(
      `[pageContext] ignored duplicate owner for "${field}": "${firstOwner}" already owns it; ` +
        `"${secondOwner}" was rejected.`,
    );
  }
};

const deduplicateResources = (resources: PageContextResource[]): PageContextResource[] => {
  const byIdentity = new Map<string, PageContextResource>();
  resources.forEach((resource) => {
    const key = `${resource.type}:${resource.id}`;
    const current = byIdentity.get(key);
    if (!current) {
      byIdentity.set(key, resource);
      return;
    }
    byIdentity.set(key, {
      ...current,
      ...(resource.name ? { name: resource.name } : {}),
      role:
        RESOURCE_ROLE_PRIORITY[resource.role] > RESOURCE_ROLE_PRIORITY[current.role]
          ? resource.role
          : current.role,
    });
  });
  return [...byIdentity.values()];
};

const addNavigation = (
  owner: string,
  contribution: PageContextContribution,
  parts: CompositionParts,
): void => {
  contribution.navigation?.forEach(({ level, key, label }) => {
    if (!key || !Number.isInteger(level) || level < 0) {
      return;
    }
    const currentOwner = parts.navigationOwners.get(level);
    if (currentOwner) {
      warnDuplicateOwner(`navigation.${level}`, currentOwner, owner);
      return;
    }
    parts.navigationOwners.set(level, owner);
    parts.navigationByLevel.set(level, { key, ...(label ? { label } : {}) });
  });
};

const addState = (
  owner: string,
  contribution: PageContextContribution,
  parts: CompositionParts,
): void => {
  (Object.keys(contribution.state ?? {}) as Array<keyof PageContextState>).forEach((field) => {
    const value = contribution.state?.[field];
    if (value === undefined) {
      return;
    }
    const currentOwner = parts.stateOwners.get(field);
    if (currentOwner) {
      warnDuplicateOwner(`state.${field}`, currentOwner, owner);
      return;
    }
    parts.stateOwners.set(field, owner);
    Object.assign(parts.state, { [field]: value });
  });
};

const addPresentation = (
  owner: string,
  contribution: PageContextContribution,
  parts: CompositionParts,
): void => {
  const integration = contribution.presentation?.integration;
  if (!integration) {
    return;
  }
  if (parts.integrationOwner) {
    warnDuplicateOwner('presentation.integration', parts.integrationOwner, owner);
    return;
  }
  parts.integrationOwner = owner;
  parts.integration = integration;
};

/** Compose route facts and rendered-page contributions into one deterministic snapshot. */
export const composePageContext = (
  route: ResolvedPageContextRoute | undefined,
  pageScope: string | undefined,
  registeredContributions: Map<PageContextContributorId, RegisteredPageContextContribution>,
  subjectName?: string,
): PageContextSnapshot | undefined => {
  if (!route || !pageScope) {
    return undefined;
  }

  const activeContributions = [...registeredContributions.values()]
    .filter((registration) => registration.pageScope === pageScope)
    .sort((left, right) => left.owner.localeCompare(right.owner));
  const parts: CompositionParts = {
    navigationOwners: new Map(),
    navigationByLevel: new Map(),
    stateOwners: new Map(),
    state: {},
    resources: route.resources.map((resource) =>
      resource.role === 'subject' && subjectName?.trim()
        ? { ...resource, name: subjectName.trim() }
        : resource,
    ),
  };

  activeContributions.forEach(({ owner, contribution }) => {
    addNavigation(owner, contribution, parts);
    parts.resources.push(...(contribution.resources ?? []));
    addState(owner, contribution, parts);
    addPresentation(owner, contribution, parts);
  });

  const navigation = [...parts.navigationByLevel.entries()]
    .sort(([left], [right]) => left - right)
    .map(([, item]) => item);
  const resources = deduplicateResources(parts.resources);
  const page: PageContext = {
    type: route.type,
    title: route.presentation.label,
    ...(navigation.length > 0 ? { navigation } : {}),
    ...(resources.length > 0 ? { resources } : {}),
    ...(Object.keys(parts.state).length > 0 ? { state: parts.state } : {}),
  };

  return {
    page,
    presentation: {
      ...route.presentation,
      ...(parts.integration ? { integration: parts.integration } : {}),
    },
  };
};
